<template>
  <div v-if="open" class="wg-modal-back" @click="close">
    <div class="wg-modal" role="dialog" aria-modal="true" @click.stop>
      <button class="wg-modal-x" aria-label="Close" @click="close">
        <i class="material-icons">close</i>
      </button>
      <div class="wg-modal-head">
        <div class="wg-modal-ico" :class="tone">
          <i class="material-icons-outlined">{{ icon }}</i>
        </div>
        <h3>{{ title }}</h3>
      </div>
      <p>
        <slot>{{ body }}</slot>
      </p>
      <input
        v-if="requireText"
        class="wg-input"
        :placeholder="requireText"
        v-model="typed"
        type="text"
      />
      <div class="acts">
        <button class="wg-mbtn ghost" :disabled="busy" @click="close">{{ cancelLabel }}</button>
        <button
          class="wg-mbtn"
          :class="confirmTone"
          :disabled="Boolean(busy || (requireText && typed !== requireText))"
          @click="$emit('confirm')"
        >
          <span v-if="busy" class="wg-spin"></span>
          {{ busy && busyLabel ? busyLabel : confirmLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Design-mock confirm modal (centered card, icon + title, pill buttons).
 * `requireText` gates the confirm button until the user types it exactly.
 */
export default {
  name: 'WgModal',
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, required: true },
    body: { type: String, default: '' },
    icon: { type: String, default: 'info' },
    /** danger | brand */
    tone: { type: String, default: 'brand' },
    confirmLabel: { type: String, default: 'Confirm' },
    /** solid | danger | solid-blue */
    confirmTone: { type: String, default: 'solid' },
    cancelLabel: { type: String, default: 'Cancel' },
    requireText: { type: String, default: '' },
    busy: { type: Boolean, default: false },
    busyLabel: { type: String, default: '' },
  },
  data () {
    return {
      typed: '',
    }
  },
  watch: {
    open (value) {
      if (value) this.typed = ''
    },
  },
  methods: {
    close () {
      if (this.busy) return
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

.wg-modal-back {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 24px;
  animation: wgfade 0.15s ease;
}

@keyframes wgfade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.wg-modal {
  position: relative;
  background: #fff;
  border-radius: 14px;
  box-shadow: $shadow-menu;
  width: 100%;
  max-width: 470px;
  padding: 30px 32px 26px;
  animation: wgpop 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.2);

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: $pop-teal;
    margin: 0;
    line-height: 1.25;
  }

  p {
    font-size: 0.85rem;
    line-height: 1.55;
    color: $pop-body;
    margin: 0 0 20px;
  }

  .wg-input {
    margin-bottom: 14px;
  }

  .acts {
    display: flex;
    justify-content: flex-end;
    gap: 10px;

    > * {
      flex: 0 0 auto;
    }
  }
}

@keyframes wgpop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.wg-modal-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
  padding-right: 26px;
}

.wg-modal-x {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: $pop-ink-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: $pop-box-bg;
    color: $pop-teal;
  }

  .material-icons {
    font-size: 1.2rem;
  }
}

.wg-modal-ico {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  .material-icons-outlined {
    font-size: 1.5rem;
  }

  &.danger {
    background: $pop-danger-bg;
    color: $pop-danger;
  }

  &.brand {
    background: $pop-accent-bg;
    color: $pop-accent;
  }
}

.wg-mbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 24px;
  border-radius: 30px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  transition: opacity 0.2s, background 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &.ghost {
    background: transparent;
    color: $pop-teal;
    border-color: $pop-border;

    &:hover:not(:disabled) {
      background: $pop-box-bg;
      opacity: 1;
    }
  }

  &.solid {
    background: $pop-accent;
    color: #fff;
  }

  &.danger {
    background: $pop-danger;
    color: #fff;
  }

  &.solid-blue {
    background: $pop-link;
    color: #fff;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}
</style>
